import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Chip,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  Forum as ForumIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { forumAPI, coursesAPI, lessonsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import Card from '../components/UIKit/Card';
import TextField from '../components/UIKit/TextField';
import TopicCard from '../components/TopicCard';
import SearchInput from '../components/UIKit/SearchInput';
import { getUserAvatarSrc } from '../utils/avatarUtils';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

const Forum = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation(['forum', 'common']);
  
  const [loading, setLoading] = useState(true);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [error, setError] = useState(null);
  const [topicsMap, setTopicsMap] = useState(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTopics, setHasMoreTopics] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [forumStatistics, setForumStatistics] = useState({ totalTopics: 0, totalComments: 0 });
  const [userActivityStats, setUserActivityStats] = useState({ topics: 0, comments: 0 });

  // Флаги загрузки для предотвращения избыточных запросов
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [lessonsLoaded, setLessonsLoaded] = useState(false);
  
  // State for specific topic view
  const { id: topicId } = useParams();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loadingTopic, setLoadingTopic] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');

  // Function to handle search query changes
  const handleSearchChange = useCallback((newQuery) => {
    setSearchQuery(newQuery);
    setCurrentPage(1);
    setHasMoreTopics(true);
    setShouldFetchData(true);
  }, []);
  const [selectedLesson, setSelectedLesson] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, popular
  const [openTopicDialog, setOpenTopicDialog] = useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicContent, setNewTopicContent] = useState('');
  const [newComment, setNewComment] = useState('');
  const [selectedTopicCourse, setSelectedTopicCourse] = useState('');
  const [selectedTopicLesson, setSelectedTopicLesson] = useState('');
  const [shouldFetchData, setShouldFetchData] = useState(true);

  // Computed values for filtered options - мемоизированы для оптимизации
  // Всегда показываем все курсы и уроки для возможности одновременного выбора
  const topics = useMemo(() => Array.from(topicsMap.values()), [topicsMap]);
  const courseOptions = useMemo(() => {
    return courses.map(course => ({ value: course._id, label: course.title }));
  }, [courses]);

  const lessonOptions = useMemo(() => {
    return lessons.map(lesson => ({ value: lesson._id, label: lesson.title }));
  }, [lessons]);

  // Мемоизированные функции для вычисления названий курсов и уроков
  const getTopicCourseTitle = useMemo(() => {
    return (topic) => {
      const courseId = topic.course?._id || topic.course;
      const course = courses.find((c) => c._id === courseId);
      return course?.title || topic.course?.title || t('forumPage.topics.course');
    };
  }, [courses, t]);

  // Функция для получения slug курса из топика
  const getTopicCourseSlug = useMemo(() => {
    return (topic) => {
      if (topic.course?.slug) {
        return topic.course.slug;
      }
      const courseId = topic.course?._id || topic.course;
      const course = courses.find((c) => c._id === courseId);
      return course?.slug || courseId; // fallback к ID если slug нет
    };
  }, [courses]);

  const getTopicLessonTitle = useMemo(() => {
    return (topic) => {
      const lessonId = topic.lesson?._id || topic.lesson;
      const lesson = lessons.find((l) => l._id === lessonId);
      return lesson?.title || topic.lesson?.title || `Lesson ${lessonId?.slice(-4) || 'Unknown'}`;
    };
  }, [lessons]);

  // Function to update forum statistics
  const updateForumStatistics = useCallback(async () => {
    try {
      const statisticsResponse = await forumAPI.getStatistics();
      const statsData = statisticsResponse.data;
      
      // Set general forum statistics
      setForumStatistics({
        totalTopics: statsData.general?.totalTopics || 0,
        totalComments: statsData.general?.totalComments || 0
      });
      
      // Set user-specific statistics if available
      if (statsData.user && isAuthenticated) {
        setUserActivityStats({
          topics: statsData.user.topicsCreated || 0,
          comments: statsData.user.commentsWritten || 0
        });
      }
    } catch (err) {
      console.error('Error updating forum statistics:', err);
    }
  }, [isAuthenticated]);

  // Removed handleTabChange function - no tabs needed

  // Load more topics function
  const loadMoreTopics = useCallback(async () => {
    if (loadingMore || !hasMoreTopics) return;
    
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      
      // Prepare parameters for the new API
      const apiParams = {
        page: nextPage,
        limit: 5,
        sortBy
      };
      
      // Add search parameter if search query exists
      if (searchQuery && searchQuery.trim() !== '') {
        apiParams.search = searchQuery;
      }
      
      // Add courseId parameter if we want to filter by specific course
      if (selectedCourse !== 'all') {
        apiParams.courseId = selectedCourse;
      }
      
      // Add lessonId parameter if we want to filter by specific lesson
      if (selectedLesson !== 'all') {
        apiParams.lessonId = selectedLesson;
      }
      
      const response = await forumAPI.getAllTopics(apiParams);
      const newTopics = response.data.topics || [];
      
      // No need for client-side filtering anymore - backend handles it
      setTopicsMap(prevMap => {
        const newMap = new Map(prevMap);
        newTopics.forEach(topic => {
          newMap.set(topic._id, topic);
        });
        return newMap;
      });
      setCurrentPage(nextPage);
      setHasMoreTopics(newTopics.length === 5);
      
    } catch (err) {
      console.error('Error loading more topics:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMoreTopics, currentPage, selectedCourse, selectedLesson, searchQuery, sortBy]);

  const handleOpenTopicDialog = useCallback(() => {
    if (!isAuthenticated) {
      toast.info(t('forumPage.auth.loginRequiredTopic'));
      navigate('/login');
      return;
    }
    setOpenTopicDialog(true);
  }, [isAuthenticated, navigate, t]);

  const handleCloseTopicDialog = useCallback(() => {
    setOpenTopicDialog(false);
    setNewTopicTitle('');
    setNewTopicContent('');
    setSelectedTopicCourse('');
    setSelectedTopicLesson('');
  }, []);

  const handleTopicCourseChange = useCallback((e) => {
    const courseId = e.target.value;
    setSelectedTopicCourse(courseId);
    // Не сбрасываем урок - позволяем одновременный выбор
  }, []);

  const handleTopicLessonChange = useCallback((e) => {
    const lessonId = e.target.value;
    setSelectedTopicLesson(lessonId);
    // Не сбрасываем курс - позволяем одновременный выбор
  }, []);

  const handleCreateTopic = useCallback(async () => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequiredTopic'));
      navigate('/login');
      return;
    }

    try {
      if (!newTopicTitle.trim() || !newTopicContent.trim()) {
        toast.error(t('forumPage.validation.titleRequired'));
        return;
      }

      // Validation: either course or lesson, but not both
      if (!selectedTopicCourse && !selectedTopicLesson) {
        toast.error(t('forumPage.validation.courseOrLessonRequired'));
        return;
      }

      const data = {
        title: newTopicTitle,
        content: newTopicContent,
        course: selectedTopicCourse || null,
        lesson: selectedTopicLesson || null,
      };

      const response = await forumAPI.createTopic(data);
      
      
      // Add new topic to local state for immediate update
      const newTopic = response?.data ? {
        ...response.data,
        // Ensure we have the content from our local state
        title: newTopicTitle,
        content: newTopicContent,
      } : {
        _id: response?.data?._id || Date.now().toString(),
        title: newTopicTitle,
        content: newTopicContent,
        author: {
          _id: user._id,
          username: user.userMeta?.fullName,
          avatar: user.avatar,
        },
        createdAt: new Date().toISOString(),
        likes: [],
        comments: [],
        course: null,
        isLocked: false,
        isPinned: false,
        tags: [],
        views: 0,
      };
      
      setTopicsMap(prevMap => {
        const newMap = new Map(prevMap);
        newMap.set(newTopic._id, newTopic);
        return newMap;
      });
      
      toast.success(t('forumPage.success.topicCreated'));
      handleCloseTopicDialog();
      setShouldFetchData(true);
      
      // Update statistics after creating topic
      await updateForumStatistics();
    } catch (err) {
      toast.error(t('forumPage.errors.createTopicError'));
      console.error('Ошибка при создании темы:', err);
    }
  }, [isAuthenticated, user, navigate, t, newTopicTitle, newTopicContent, selectedTopicCourse, selectedTopicLesson, handleCloseTopicDialog, updateForumStatistics]);

  const handleCloseCommentDialog = useCallback(() => {
    setOpenCommentDialog(false);
    setNewComment('');
    setSelectedTopic(null);
  }, []);

  const handleAddComment = useCallback(async () => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequiredComment'));
      navigate('/login');
      return;
    }

    // Check if user has rated the course
    if (selectedTopic && (!selectedTopic.currentUserRating || selectedTopic.currentUserRating === 0)) {
      toast.warning(t('forumPage.validation.ratingRequired'));
      return;
    }

    try {
      if (!newComment.trim()) {
        toast.error(t('forumPage.validation.commentRequired'));
        return;
      }

      const response = await forumAPI.addComment(selectedTopic._id, {
        content: newComment,
      });


      // Update local state using Map
      setTopicsMap(prevMap => {
        const newMap = new Map(prevMap);
        const topic = newMap.get(selectedTopic._id);
        if (topic) {
          const currentComments = Array.isArray(topic.comments) ? topic.comments : [];

          // Use server response data if available, otherwise create fallback
          const newCommentObj = response?.data ? {
            ...response.data,
            // Ensure we have the content from our local state
            content: newComment,
          } : {
            _id: response?.data?._id || Date.now().toString(),
            content: newComment,
            author: {
              _id: user._id,
              username: user.userMeta?.fullName,
              avatar: user.avatar,
            },
            createdAt: new Date().toISOString(),
            likes: [],
            replies: [],
          };

          newMap.set(selectedTopic._id, {
            ...topic,
            comments: [...currentComments, newCommentObj],
          });
        }
        return newMap;
      });

      toast.success(t('forumPage.success.commentAdded'));
      handleCloseCommentDialog();

      // Update statistics after adding comment
      await updateForumStatistics();
    } catch (err) {
      console.error('Error adding comment:', err);

      // Check if error is related to rating requirement
      if (err.response?.data?.code === 'RATING_REQUIRED') {
        toast.warning(err.response.data.message || t('forumPage.validation.ratingRequired'));
      } else {
        toast.error(t('forumPage.errors.addCommentError'));
      }
    }
  }, [isAuthenticated, user, navigate, t, newComment, selectedTopic, handleCloseCommentDialog, updateForumStatistics]);

  // Function to load specific topic
  const loadTopic = useCallback(async (topicId) => {
    try {
      setLoadingTopic(true);
      const response = await forumAPI.getTopicById(topicId);
      setSelectedTopic(response.data);
    } catch (err) {
      console.error('Error loading topic:', err);
      toast.error(t('forumPage.errors.loadTopicError'));
    } finally {
      setLoadingTopic(false);
    }
  }, [t]);



  const handleCourseChange = useCallback((e) => {
    const courseId = e.target.value;

    // Only proceed if the value actually changed
    if (courseId === selectedCourse) {
      return;
    }

    setSelectedCourse(courseId);
    setCurrentPage(1);
    setHasMoreTopics(true);
    setShouldFetchData(true);
  }, [selectedCourse]);

  const handleLessonChange = useCallback((e) => {
    const lessonId = e.target.value;

    // Only proceed if the value actually changed
    if (lessonId === selectedLesson) {
      return;
    }

    setSelectedLesson(lessonId);
    setCurrentPage(1);
    setHasMoreTopics(true);
    setShouldFetchData(true);
  }, [selectedLesson]);

  const handleSortChange = useCallback((e) => {
    const sortValue = e.target.value;
    setSortBy(sortValue);
    setCurrentPage(1);
    setHasMoreTopics(true);
    setShouldFetchData(true);
  }, []);

  // Optimized functions for updating reactions using Map
  const updateTopicReactions = useCallback((topicId, reactions = {}) => {
    setTopicsMap(prev => {
      const newMap = new Map(prev);
      const topic = newMap.get(topicId);
      if (topic) {
        newMap.set(topicId, {
          ...topic,
          likes: Array.isArray(reactions.likes) ? reactions.likes : topic.likes,
          dislikes: Array.isArray(reactions.dislikes) ? reactions.dislikes : (topic.dislikes || []),
        });
      }
      return newMap;
    });

    setSelectedTopic(prev => {
      if (prev?._id === topicId) {
        return {
          ...prev,
          likes: Array.isArray(reactions.likes) ? reactions.likes : prev.likes,
          dislikes: Array.isArray(reactions.dislikes) ? reactions.dislikes : (prev.dislikes || []),
        };
      }
      return prev;
    });
  }, [setSelectedTopic]);

  const updateCommentReactions = useCallback((topicId, commentId, reactions = {}) => {
    setTopicsMap(prev => {
      const newMap = new Map(prev);
      const topic = newMap.get(topicId);
      if (topic) {
        const updatedComments = (topic.comments || []).map(comment => {
          if (comment._id === commentId) {
            return {
              ...comment,
              likes: Array.isArray(reactions.likes) ? reactions.likes : comment.likes,
              dislikes: Array.isArray(reactions.dislikes) ? reactions.dislikes : (comment.dislikes || []),
            };
          }
          return comment;
        });
        newMap.set(topicId, { ...topic, comments: updatedComments });
      }
      return newMap;
    });

    setSelectedTopic(prev => {
      if (prev?._id === topicId) {
        const updatedComments = (prev.comments || []).map(comment => {
          if (comment._id === commentId) {
            return {
              ...comment,
              likes: Array.isArray(reactions.likes) ? reactions.likes : comment.likes,
              dislikes: Array.isArray(reactions.dislikes) ? reactions.dislikes : (comment.dislikes || []),
            };
          }
          return comment;
        });
        return { ...prev, comments: updatedComments };
      }
      return prev;
    });
  }, [setSelectedTopic]);

  const handleLike = useCallback((type, id, reactionData = {}) => {
    if (!isAuthenticated || !user) {
      toast.info(t('forumPage.auth.loginRequired'));
      navigate('/login');
      return;
    }

    try {
      if (type === 'topics') {
        const topicId = reactionData.topicId || id;
        updateTopicReactions(topicId, reactionData);
      } else if (type === 'comments') {
        const commentId = reactionData.commentId || id;
        const topicId = reactionData.topicId;

        if (topicId) {
          updateCommentReactions(topicId, commentId, reactionData);
        } else {
          const fallbackTopic = topics.find(t => t.comments?.some(c => c._id === commentId));
          if (fallbackTopic) {
            updateCommentReactions(fallbackTopic._id, commentId, reactionData);
          }
        }
      }
    } catch (err) {
      toast.error(t('forumPage.errors.likeError'));
      console.error('Error updating reaction:', err);
    }
  }, [isAuthenticated, user, navigate, t, updateTopicReactions, updateCommentReactions, topics]);

  // Объединенный useEffect для загрузки всех данных
  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Загружаем курсы и уроки параллельно, если они еще не загружены
        const promises = [];

        if (!coursesLoaded) {
          promises.push(
            coursesAPI.getAllShort().then(response => {
              setCourses(response.data.courses || []);
              setCoursesLoaded(true);
            }).catch(err => {
              console.error('Error loading courses:', err);
              setCourses([]);
              setCoursesLoaded(true);
            })
          );
        }

        if (!lessonsLoaded) {
          promises.push(
            lessonsAPI.getAll({ all: true, isCommon: 1 }).then(response => {
              if (response.data && response.data.success && response.data.data) {
                setLessons(response.data.data);
              } else {
                setLessons([]);
              }
              setLessonsLoaded(true);
            }).catch(err => {
              console.error('Error loading lessons:', err);
              setLessons([]);
              setLessonsLoaded(true);
            })
          );
        }

        // Ждем завершения загрузки курсов и уроков
        if (promises.length > 0) {
          await Promise.all(promises);
        }

        // Загружаем статистику форума
        await updateForumStatistics();

        setLoading(false);
      } catch (err) {
        setError('Ошибка при загрузке данных');
        console.error('Ошибка при загрузке данных:', err);
        setLoading(false);
      }
    };

    loadAllData();
  }, [coursesLoaded, lessonsLoaded, updateForumStatistics]); // Загружаем только при монтировании компонента

  // Load topics based on filters and search
  useEffect(() => {
    const loadTopics = async () => {
      if (!shouldFetchData) return;
      try {
        setLoadingTopics(true);
        setError(null);

        // Fetch forum data based on current state using new unified API
        
        // Prepare parameters for the new API
        const apiParams = {
          page: 1,
          limit: 5,
          sortBy
        };
        
        // Add search parameter if search query exists
        if (searchQuery && searchQuery.trim() !== '') {
          apiParams.search = searchQuery;
        }
        
        // Add courseId parameter if we want to filter by specific course
        if (selectedCourse !== 'all') {
          apiParams.courseId = selectedCourse;
        }
        
        // Add lessonId parameter if we want to filter by specific lesson
        if (selectedLesson !== 'all') {
          apiParams.lessonId = selectedLesson;
        }
        
        const response = await forumAPI.getAllTopics(apiParams);
        const topics = response.data.topics || [];
        
        // No need for client-side filtering anymore - backend handles it
        setTopicsMap(prevMap => {
          const newMap = new Map();
          topics.forEach(topic => {
            newMap.set(topic._id, topic);
          });
          return newMap;
        });
        setHasMoreTopics(topics.length === 5);
        
        setCurrentPage(1);
        setShouldFetchData(false);
      } catch (err) {
        setError('Ошибка при загрузке данных форума');
        console.error('Ошибка при загрузке данных форума:', err);
        setTopicsMap(new Map());
      } finally {
        setLoadingTopics(false);
      }
    };

    loadTopics();
  }, [
    shouldFetchData,
    selectedCourse,
    selectedLesson,
    searchQuery,
    sortBy
  ]);



  // Initialize data fetching on component mount
  useEffect(() => {
    setShouldFetchData(true);
  }, []);

  // Calculate user activity stats when user changes
  useEffect(() => {
    if (!user?._id) {
      // Reset stats when user logs out
      setUserActivityStats({ topics: 0, comments: 0 });
    }
  }, [user?._id]);

  // Load specific topic when topicId is in URL
  useEffect(() => {
    if (topicId) {
      loadTopic(topicId);
    } else {
      setSelectedTopic(null);
    }
  }, [topicId, loadTopic]);

  // Handle scroll for lazy loading
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        loadMoreTopics();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, hasMoreTopics, loadingMore, searchQuery, selectedCourse, selectedLesson, sortBy, loadMoreTopics]);

  // Remove the global loading check - we'll handle loading per section

  return (
    <Container sx={{ py: 4 }}>
      <Box>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            mb: { xs: 2, md: 4 },
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <ForumIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          {t('forumPage.title')}
        </Typography>

        <Grid container spacing={3}>
          {/* Левая колонка с фильтрами */}
          <Grid item xs={12} md={5} xl={4} xxl={3}>
            <Box>
              <Card sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('common:filters')}
                </Typography>
                <Autocomplete
                  fullWidth
                  options={[
                    { value: 'all', label: t('forumPage.filters.allCourses') },
                    ...courseOptions
                  ]}
                  value={selectedCourse !== 'all' ? 
                    courseOptions.find(c => c.value === selectedCourse) || 
                        { value: 'all', label: t('forumPage.filters.allCourses') } :
                    { value: 'all', label: t('forumPage.filters.allCourses') }
                  }
                  onChange={(event, newValue) => {
                    const courseId = newValue === null ? 'all' : (newValue?.value || 'all');
                    
                    // Handle clear action directly in onChange
                    if (newValue === null || newValue === undefined) {
                      setSelectedCourse('all');
                      setCurrentPage(1);
                      setHasMoreTopics(true);
                      setShouldFetchData(true);
                    } else {
                      handleCourseChange({ target: { value: courseId } });
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                  }}
                  loading={loading}
                  clearOnEscape
                  clearOnBlur={false}
                  disableClearable={false}
                  freeSolo={false}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('forumPage.filters.selectCourse')}
                  sx={{ mb: 2 }}
                    />
                  )}
                />
               
                <Autocomplete
                  fullWidth
                  options={[
                    { value: 'all', label: t('forumPage.filters.allLessons') },
                    ...lessonOptions
                  ]}
                  value={selectedLesson !== 'all' ? 
                    lessonOptions.find(l => l.value === selectedLesson) || 
                        { value: 'all', label: t('forumPage.filters.allLessons') } :
                    { value: 'all', label: t('forumPage.filters.allLessons') }
                  }
                  onChange={(event, newValue) => {
                    const lessonId = newValue === null ? 'all' : (newValue?.value || 'all');
                    
                    // Handle clear action directly in onChange
                    if (newValue === null || newValue === undefined) {
                      setSelectedLesson('all');
                      setCurrentPage(1);
                      setHasMoreTopics(true);
                      setShouldFetchData(true);
                    } else {
                      handleLessonChange({ target: { value: lessonId } });
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                  }}
                  loading={loading}
                  clearOnEscape
                  clearOnBlur={false}
                  disableClearable={false}
                  freeSolo={false}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('forumPage.filters.selectLesson')}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <SearchInput
                  label={t('forumPage.filters.searchLabel')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t('forumPage.filters.searchPlaceholder')}
                  debounceTime={300}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                
                <Autocomplete
                  fullWidth
                  options={[
                    { value: 'newest', label: t('forumPage.filters.newest') },
                    { value: 'oldest', label: t('forumPage.filters.oldest') },
                    { value: 'popular', label: t('forumPage.filters.popular') }
                  ]}
                  value={{ value: sortBy, label: t(`forumPage.filters.${sortBy}`) }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleSortChange({ target: { value: newValue.value } });
                    }
                  }}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('forumPage.filters.sortBy')}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Card>
              {isAuthenticated && (
                <Card sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={handleOpenTopicDialog}
                    sx={{ py: 1 }}
                  >
                    {t('forumPage.actions.createTopic')}
                  </Button>
                </Card>
              )}

              <Card>
                <Typography variant="h6" gutterBottom>
                  {t('common:statistics')}
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <ForumIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1">{t('forumPage.topics.topicsOnForum')}</Typography>
                      }
                      secondary={
                        <Typography variant="h6" color="primary">
                          {forumStatistics.totalTopics}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.light' }}>
                        <CommentIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1">{t('forumPage.topics.comments')}</Typography>
                      }
                      secondary={
                        <Typography variant="h6" color="secondary">
                          {forumStatistics.totalComments}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {isAuthenticated && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'success.light' }}>
                          <SchoolIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body1">
                            {t('forumPage.userActivity.title')}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {t('forumPage.userActivity.topicsAndComments', {
                              topics: userActivityStats.topics,
                              comments: userActivityStats.comments
                            })}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Card>
            </Box>
          </Grid>

          {/* Правая колонка с темами */}
          <Grid item xs={12} md={7} xl={8} xxl={9}>
            <Card>
              {/* Removed tabs - always show all topics */}

              {loadingTopics ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('forumPage.errors.loadError')}
                </Alert>
              ) : selectedTopic ? (
                // Display specific topic
                <Box>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        // Check if user came from profile
                        if (location.state?.from === 'profile') {
                          navigate('/profile');
                        } else {
                          navigate('/forum');
                        }
                      }}
                      sx={{ mb: 2 }}
                    >
                      ← {t('common:actions.back')}
                    </Button>
                  </Box>
                  
                  {loadingTopic ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Card sx={{ mb: 3 }}>
                      <Box sx={{ p: 3 }}>
                        <Typography variant="h4" sx={{ mb: 2 }}>
                          {selectedTopic.title}
                        </Typography>
                        
                        {/* Course/Lesson info */}
                        {(selectedTopic.course || selectedTopic.lesson) && (
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            {selectedTopic.course && (
                              <Chip
                                icon={<SchoolIcon />}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {t('forumPage.topics.course')}:
                                    </Typography>
                                    <Typography variant="body2">
                                      {getTopicCourseTitle(selectedTopic)}
                                    </Typography>
                                  </Box>
                                }
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/courses/${getTopicCourseSlug(selectedTopic)}`, { state: { from: 'forum' } })}
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                            {selectedTopic.lesson && (
                              <Chip
                                icon={<MenuBookIcon />}
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {t('forumPage.topics.lesson')}:
                                    </Typography>
                                    <Typography variant="body2">
                                      {getTopicLessonTitle(selectedTopic)}
                                    </Typography>
                                  </Box>
                                }
                                size="small"
                                color="secondary"
                                onClick={() => {
                                  const lessonSlug = selectedTopic.lesson?.slug || selectedTopic.lesson?._id || selectedTopic.lesson;
                                  navigate(`/lessons/${lessonSlug}`, { state: { from: 'forum' } });
                                }}
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            src={getUserAvatarSrc(selectedTopic.author)}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/profile/${selectedTopic.author._id}`)}
                          />
                          <Box>
                            <Typography variant="subtitle2">
                              {selectedTopic.author?.userMeta?.fullName || 'Unknown User'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDistanceToNow(new Date(selectedTopic.createdAt), { addSuffix: true, locale: ru })}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                          {selectedTopic.content}
                        </Typography>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          {t('forumPage.topics.comments')} ({selectedTopic.comments?.length || 0})
                        </Typography>
                        
                        {selectedTopic.comments?.map((comment) => (
                          <Box key={comment._id} sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Avatar
                                src={getUserAvatarSrc(comment.author)}
                                sx={{ width: 32, height: 32 }}
                              />
                              <Box>
                                <Typography variant="subtitle2">
                                  {comment.author?.userMeta?.fullName || 'Unknown User'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true, locale: ru })}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {comment.content}
                            </Typography>
                          </Box>
                        ))}
                        
                        {isAuthenticated && (
                          <Box sx={{ mt: 3 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              placeholder={t('forumPage.actions.addComment')}
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              sx={{ mb: 2 }}
                              disabled={!selectedTopic.currentUserRating || selectedTopic.currentUserRating === 0}
                            />
                            <Button
                              variant="contained"
                              onClick={handleAddComment}
                              disabled={!newComment.trim() || !selectedTopic.currentUserRating || selectedTopic.currentUserRating === 0}
                            >
                              {t('forumPage.actions.addComment')}
                            </Button>
                            {(!selectedTopic.currentUserRating || selectedTopic.currentUserRating === 0) && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                                {t('forumPage.validation.ratingRequired')}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Card>
                  )}
                </Box>
              ) : topics.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('forumPage.topics.noTopics')}
                </Alert>
              ) : (
                <List>
                  {topics.map((topic) => (
                    <TopicCard
                      key={topic._id}
                      topic={topic}
                      onLike={handleLike}
                      onAddComment={async (topicId, commentData) => {
                        if (!isAuthenticated || !user) {
                          toast.info(t('forumPage.auth.loginRequiredComment'));
                          navigate('/login');
                          return;
                        }
                        
                        const response = await forumAPI.addComment(topicId, commentData);
                        
                        // Update local state using Map
                        setTopicsMap(prevMap => {
                          const newMap = new Map(prevMap);
                          const topic = newMap.get(topicId);
                          if (topic) {
                            const currentComments = Array.isArray(topic.comments) ? topic.comments : [];
                            
                            // Use server response data if available, otherwise create fallback
                            const newCommentObj = response?.data ? {
                              ...response.data,
                              // Ensure we have the content from our local state
                              content: commentData.content,
                            } : {
                              _id: response?.data?._id || Date.now().toString(),
                              content: commentData.content,
                              author: {
                                _id: user._id,
                                username: user.userMeta?.fullName,
                                avatar: user.avatar,
                              },
                              createdAt: new Date().toISOString(),
                              likes: [],
                              replies: [],
                            };
                            
                            newMap.set(topicId, {
                              ...topic,
                              comments: [...currentComments, newCommentObj],
                            });
                          }
                          return newMap;
                        });
                        
                        // Update statistics after adding comment
                        await updateForumStatistics();
                      }}
                    />
                  ))}
                          </List>
              )}
            </Card>
            
            {/* Load more button */}
            {hasMoreTopics && topics.length > 0 && !loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={loadMoreTopics}
                  disabled={loadingMore}
                >
                  {t('forumPage.actions.loadMore')}
                </Button>
              </Box>
            )}
            
            {/* Loading more indicator */}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {/* No more topics indicator */}
            {!hasMoreTopics && topics.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 2, py: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('forumPage.topics.noMoreTopics')}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Диалоги */}
      <Dialog
        open={openTopicDialog}
        onClose={handleCloseTopicDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">{t('forumPage.dialogs.createTopic.title')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              label={t('forumPage.dialogs.createTopic.topicTitle')}
              placeholder={t('forumPage.dialogs.createTopic.topicTitle')}
              fullWidth
              value={newTopicTitle}
              onChange={(e) => setNewTopicTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            
            {/* Course Selector */}
            <Autocomplete
              fullWidth
              options={courses.map(course => ({ value: course._id, label: course.title }))}
              value={courses.find(c => c._id === selectedTopicCourse) ?
                { value: selectedTopicCourse, label: courses.find(c => c._id === selectedTopicCourse).title } :
                null
              }
              onChange={(event, newValue) => {
                const courseId = newValue === null ? '' : (newValue?.value || '');
                handleTopicCourseChange({ target: { value: courseId } });
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'clear') {
                  handleTopicCourseChange({ target: { value: '' } });
                }
              }}
              disabled={!!selectedTopicLesson} // Отключаем, если выбран урок
              loading={loading}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
            <TextField
                  {...params}
                  label={t('forumPage.dialogs.createTopic.selectCourse')}
                  placeholder={selectedTopicLesson ? t('forumPage.dialogs.createTopic.courseDisabledWhenLessonSelected') : t('forumPage.dialogs.createTopic.coursePlaceholder')}
                  sx={{ mb: 2 }}
                />
              )}
            />

            {/* Lesson Selector */}
            <Autocomplete
              fullWidth
              options={lessons.map(lesson => ({ value: lesson._id, label: lesson.title }))}
              value={lessons.find(l => l._id === selectedTopicLesson) ?
                { value: selectedTopicLesson, label: lessons.find(l => l._id === selectedTopicLesson).title } :
                null
              }
              onChange={(event, newValue) => {
                const lessonId = newValue === null ? '' : (newValue?.value || '');
                handleTopicLessonChange({ target: { value: lessonId } });
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'clear') {
                  handleTopicLessonChange({ target: { value: '' } });
                }
              }}
              disabled={!!selectedTopicCourse} // Отключаем, если выбран курс
              loading={loading}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('forumPage.dialogs.createTopic.selectLesson')}
                  placeholder={selectedTopicCourse ? t('forumPage.dialogs.createTopic.lessonDisabledWhenCourseSelected') : t('forumPage.dialogs.createTopic.lessonPlaceholder')}
                  sx={{ mb: 2 }}
                />
              )}
            />
            
            <TextField
              label={t('forumPage.dialogs.createTopic.topicContent')}
              placeholder={t('forumPage.dialogs.createTopic.topicContent')}
              fullWidth
              multiline
              rows={4}
              value={newTopicContent}
              onChange={(e) => setNewTopicContent(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTopicDialog} color="secondary">
            {t('forumPage.dialogs.createTopic.cancel')}
          </Button>
          <Button onClick={handleCreateTopic} variant="contained" color="primary">
            {t('forumPage.dialogs.createTopic.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCommentDialog}
        onClose={handleCloseCommentDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">{t('forumPage.dialogs.addComment.title')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Вы комментируете тему: {selectedTopic?.title}
            {selectedTopic?.author && (
              <>
                {' '}<span>автор: </span>
                <span 
                  style={{ cursor: 'pointer', color: '#1976d2' }}
                  onClick={() => selectedTopic.author?._id && navigate(`/profile/${selectedTopic.author._id}`)}
                >
                  {selectedTopic.author?.userMeta?.fullName}
                </span>
              </>
            )}
          </Typography>
          <TextField
            autoFocus
            label={t('forumPage.dialogs.addComment.comment')}
            placeholder={t('forumPage.dialogs.addComment.comment')}
            fullWidth
            multiline
            rows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="text" onClick={handleCloseCommentDialog}>
            {t('forumPage.dialogs.addComment.cancel')}
          </Button>
          <Button
            onClick={handleAddComment}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {t('forumPage.dialogs.addComment.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Forum;
